import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as wlcFilterId } from '../../filters/wireless-controller-filter/WirelessControllerFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { listTooltip } from './funcs/IpmList';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { siteFilterId, statusFilterId, timeRangeFilterId } from '../../filters';

const title = t`Wireless Controllers`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        deviceType: 'wirelesscontroller',
        entityFields: ['vendor', 'name', 'deviceModel', 'hwVersion', 'description'],
        host: true,
        metrics: ['status', 'apCounts', 'radioCounts'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Name`,
        sortable: true,
        mapping: 'device.name',
        getTooltip: listTooltip('device.name', 'device.description'),
        width: 220,
        link: {
            target: '../wireless-controller-detail',
            mappedFilterParams: {
                [wlcFilterId]: 'device',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Model`,
        sortProperty: 'vendor,deviceModel',
        mapper: (row) => {
            const { deviceModel, vendor } = row.device;

            return deviceModel ? `${vendor} ${deviceModel}` : vendor;
        },
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Sites`,
        sortable: false,
        mapper: (row) => {
            const sites = row.device.sites || [];
            if (sites.length === 0) return DEFAULT_INVALID_VALUE;
            return sites.map((site) => site.name).join(', ');
        },
        width: 120,
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Access Points`,
        sortable: false,
        mapping: 'apCounts.total',
        tooltip: false,
        width: 110,
        link: {
            target: '../access-points',
            mappedFilterParams: {
                [wlcFilterId]: 'device',
            },
        },
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Radios`,
        sortable: false,
        mapping: 'radioCounts.total',
        tooltip: false,
        width: 110,
        link: {
            target: '../radios',
            mappedFilterParams: {
                [wlcFilterId]: 'device',
            },
        },
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function WirelessControllerListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="device.deleted"
        />
    );
}

WirelessControllerListPanel.propTypes = panelPropTypes;

WirelessControllerListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'WirelessControllerList',
    catalogEntry: {
        category: PANEL_CATEGORIES.WIRELESS_CONTROLLER,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of wireless controllers, showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [siteFilterId, statusFilterId],
    },
})(WirelessControllerListPanel);
